/**
 * occupancy.js — the ONE place hotel in-house figures are read, saved and
 * formatted (Oct 2026).
 *
 * Storage: one '_summary_' row per night in hotel_occupancy_cache
 *   package_name = rooms occupied (stored as text), adults = guests in house.
 *
 * Every screen (Home, function sheets, daily/weekly print, Kitchen Live) uses
 * this module, so a figure saved anywhere shows everywhere straight away
 * (a browser event tells every open view to refresh).
 */
import { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabase';

export const SUMMARY_ROOM = '_summary_';
const CHANGED = 'errigal:occupancy-changed';

// Last known value per date: { rooms, guests, updatedAt } | null (not entered).
// Lets a re-mounted panel show the figure instantly instead of "Loading…".
const cache = {};

/** DB row → { rooms, guests, updatedAt }. Zero stays zero (no "|| 1"). */
export function rowToOcc(r) {
  if (!r) return null;
  return {
    rooms: parseInt(r.package_name, 10) || 0,
    guests: Number.isFinite(Number(r.adults)) ? Number(r.adults) : 0,
    updatedAt: r.updated_at || null,
  };
}

/** Fetch figures for a list of dates → { 'yyyy-MM-dd': occ | null } */
export async function fetchOccupancy(dates) {
  const uniq = [...new Set((dates || []).filter(Boolean))];
  if (!uniq.length) return {};
  const { data, error } = await supabase
    .from('hotel_occupancy_cache')
    .select('date, adults, package_name, updated_at')
    .eq('room_number', SUMMARY_ROOM)
    .in('date', uniq);
  if (error) throw error;
  const out = Object.fromEntries(uniq.map((d) => [d, null]));
  (data || []).forEach((r) => { out[r.date] = rowToOcc(r); });
  Object.assign(cache, out);
  return out;
}

/** Fetch figures for a date range (inclusive) → { date: occ } (missing dates absent) */
export async function fetchOccupancyRange(from, to) {
  const { data, error } = await supabase
    .from('hotel_occupancy_cache')
    .select('date, adults, package_name, updated_at')
    .eq('room_number', SUMMARY_ROOM)
    .gte('date', from)
    .lte('date', to);
  if (error) throw error;
  const out = {};
  (data || []).forEach((r) => { out[r.date] = rowToOcc(r); });
  return out;
}

function announce(dates) {
  try { window.dispatchEvent(new CustomEvent(CHANGED, { detail: { dates } })); } catch (_) { /* SSR / old browser */ }
}

/** Save (insert or overwrite) one night's figures. */
export async function saveOccupancy(date, rooms, guests) {
  const r = Math.max(0, parseInt(rooms, 10) || 0);
  const g = Math.max(0, parseInt(guests, 10) || 0);
  const { error } = await supabase
    .from('hotel_occupancy_cache')
    .upsert({
      date,
      room_number: SUMMARY_ROOM,
      guest_name: 'Summary',
      adults: g,
      children: 0,
      package_name: String(r),
      breakfast_incl: false,
      dinner_incl: false,
      spa_access: false,
      source: 'manual',
    }, { onConflict: 'date,room_number' });
  if (error) throw error;
  cache[date] = { rooms: r, guests: g, updatedAt: new Date().toISOString() };
  announce([date]);
}

/** Remove one night's figures (back to "not entered"). */
export async function clearOccupancy(date) {
  const { error } = await supabase
    .from('hotel_occupancy_cache')
    .delete()
    .eq('date', date)
    .eq('room_number', SUMMARY_ROOM);
  if (error) throw error;
  cache[date] = null;
  announce([date]);
}

/** Call fn whenever figures are saved anywhere in this tab. Returns unsubscribe. */
export function onOccupancyChanged(fn) {
  const h = (e) => fn(e?.detail?.dates || []);
  window.addEventListener(CHANGED, h);
  return () => window.removeEventListener(CHANGED, h);
}

/**
 * React hook: figures for a set of dates.
 * occ[date] === undefined → still loading, null → not entered, else { rooms, guests, updatedAt }.
 */
export function useOccupancy(dateKeys) {
  const key = (dateKeys || []).join(',');
  const fromCache = () => Object.fromEntries((dateKeys || []).filter((d) => d in cache).map((d) => [d, cache[d]]));
  const [occ, setOcc] = useState(fromCache);
  const [error, setError] = useState(null);

  const reload = useCallback(() => {
    let alive = true;
    fetchOccupancy(key ? key.split(',') : [])
      .then((m) => { if (alive) { setOcc(m); setError(null); } })
      .catch((err) => { console.error('[occupancy] fetch failed', err); if (alive) setError(err?.message || 'Could not load'); });
    return () => { alive = false; };
  }, [key]);

  useEffect(() => {
    setOcc(fromCache());
    const stop = reload();
    const unsub = onOccupancyChanged((dates) => {
      if (!dates.length || dates.some((d) => key.split(',').includes(d))) reload();
    });
    return () => { stop(); unsub(); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return { occ, error, reload };
}

const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

/** "8 rooms · 16 guests" — the one format used everywhere. */
export function formatOcc(o) {
  if (o === undefined) return '…';
  if (!o) return 'Not entered';
  return `${plural(o.rooms, 'room')} · ${plural(o.guests, 'guest')}`;
}

/** Sanity check while typing. Returns a warning string or ''. */
export function occWarning(rooms, guests) {
  const r = parseInt(rooms, 10), g = parseInt(guests, 10);
  if (!Number.isFinite(r) || !Number.isFinite(g)) return '';
  if (g < r) return 'Fewer guests than rooms — are rooms and guests swapped?';
  if (r > 0 && g > r * 4) return 'More than 4 guests per room — please check.';
  if (r === 0 && g > 0) return 'Guests but no rooms — please check.';
  return '';
}

/** "entered 17 Aug" for the small staleness note (screen only). */
export function enteredLabel(o) {
  if (!o?.updatedAt) return '';
  const d = new Date(o.updatedAt);
  if (Number.isNaN(d.getTime())) return '';
  return `updated ${d.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })}`;
}
